/*
  Farbwechsel ohne Seitenneuladen
  ================================

  Ein Klick auf einen Farbpunkt der Produktseite lädt sonst eine komplett neue Seite:
  2,5 s bis interaktiv, und die Auswahl von Gestellfarbe und Größe fällt auf den
  Standard zurück, weil es technisch ein anderer Artikel ist. Dieses Skript tauscht
  stattdessen nur den Produktbereich aus und überträgt die getroffene Auswahl.

  GRUNDREGEL: Die Farbpunkte bleiben echte Links. Wir fangen nur den Klick ab, und
  JEDER Fehler führt zur normalen Navigation. Dadurch kann das Skript den Shop nie
  in eine Sackgasse führen — es kann nur schneller sein oder wirkungslos bleiben.

  Zwei Dinge, ohne die es nicht funktioniert (beide gemessen, siehe
  docs/2026-08-19-farbwechsel-ohne-reload.md):

  1. Die Section-ID ist template-spezifisch («template--24506034192765__main»).
     Ein Abruf mit `section_id=main-product` liefert Status 200 und plausibles
     Markup, aber OHNE die Blöcke aus dem Template — also ohne Farbpunkte, ohne
     Fehlermeldung. Deshalb lesen wir die ID immer aus dem vorhandenen Markup.
  2. Farbgeschwister teilen dasselbe Produkt-Template, weshalb unsere Section-ID
     auch für sie gilt. Tut sie es ausnahmsweise nicht, antwortet Shopify mit 404 —
     unser Signal für den Rückfall auf normale Navigation.
*/
(function () {
  'use strict';

  var INFO_PRAEFIX = 'ProductInfo-';
  var SECTION_PRAEFIX = 'MainProduct-';

  /* Laufende Nummer gegen überholende Anfragen: Wer schnell mehrere Farben anklickt,
     löst mehrere Abrufe aus, die in beliebiger Reihenfolge zurückkommen können. Ohne
     diese Sperre konnte eine ältere Antwort eine neuere überschreiben — sichtbar als
     Mischzustand aus zwei Artikeln (fremde Optionsfelder im Kaufbereich). Nur der
     jeweils letzte Wunsch darf einsetzen. */
  var laufendeNummer = 0;

  /* Ereignis-Handler, die beim Nachziehen der Section-Skripte entstanden sind. Sie
     werden vor dem nächsten Durchlauf wieder entfernt, damit sie sich nicht anhäufen
     (siehe `skripteAusfuehren`). */
  var nachgezogeneListener = [];

  /* Pro Seitenaufruf gemerkt: Wer zwischen zwei Farben hin- und herspringt, wartet
     ab dem zweiten Mal gar nicht mehr. Schlüssel ist die jeweilige Abruf-URL.

     Begrenzt, weil ein Produktbereich rund 450 KB wiegt: Bei 15 Farbpunkten kämen
     sonst mehrere Megabyte zusammen, die bis zum Verlassen der Seite liegen bleiben.
     Zehn Einträge decken jedes realistische Hin und Her ab; darüber fällt der
     jeweils älteste heraus (Map behält die Einfügereihenfolge). */
  var SPEICHER_MAX = 10;
  var speicher = new Map();

  function merken(schluessel, wert) {
    speicher.set(schluessel, wert);
    while (speicher.size > SPEICHER_MAX) {
      speicher.delete(speicher.keys().next().value);
    }
  }

  function sectionId() {
    var info = document.querySelector('product-info[id^="' + INFO_PRAEFIX + '"]');
    return info ? info.id.slice(INFO_PRAEFIX.length) : null;
  }

  /* pCon-Produkte bleiben aussen vor: Dort hängt ein Konfigurator-iframe am Artikel,
     dessen Verhalten beim Austausch ungeklärt ist. Lieber normal navigieren. */
  function istPconSeite(sid) {
    return !!document.getElementById('KonfiguratorModal-' + sid);
  }

  /* Die aktuelle Auswahl als Zuordnung Optionsname → Wert.
     Über den Namen, nie über die Position: Die Farbartikel führen dieselben Werte
     in unterschiedlicher Reihenfolge. */
  function auswahlLesen() {
    var auswahl = {};
    document.querySelectorAll('variant-selects [data-option-name]').forEach(function (feld) {
      var name = feld.dataset.optionName;
      if (!name) return;
      var select = feld.querySelector('select');
      if (select) {
        auswahl[name] = select.value;
        return;
      }
      var radio = feld.querySelector('input[type="radio"]:checked');
      if (radio) auswahl[name] = radio.value;
    });
    return auswahl;
  }

  /* Die Variante des Zielartikels, die zur bisherigen Auswahl passt.
     «Default» wird übersprungen: Das ist die Hauptvariante (das Parent) aus
     plentymarkets, die über den Sync als eigene Variante mitkommt — ein Kunde
     würde sonst beim Farbwechsel unbemerkt darauf landen. */
  function passendeVariante(daten, auswahl) {
    if (!daten || !daten.variants || !daten.options) return null;

    var namen = daten.options.map(function (o) {
      return typeof o === 'string' ? o : o.name;
    });
    var gesucht = namen.map(function (name) {
      return auswahl[name];
    });
    if (gesucht.every(function (w) { return !w; })) return null;

    /* Gestuft statt alles-oder-nichts: Zuerst wird versucht, jede Option zu treffen.
       Gelingt das nicht, fallen die hinteren Wünsche nacheinander weg, bis etwas
       passt — die vorderen Optionen wiegen schwerer.

       Der Fall aus der Praxis: «Linoleum Pebble» führt zwar ein schwarzes Gestell,
       aber nicht in 1600 × 800 mm. Ohne Abstufung fand sich gar nichts und der Artikel
       landete auf seiner Standardvariante — mit weissem Gestell, obwohl die Farbe
       verfügbar gewesen wäre. Jetzt bleibt das schwarze Gestell erhalten und nur die
       Größe wechselt. */
    for (var tiefe = gesucht.length; tiefe > 0; tiefe--) {
      var treffer = daten.variants.find(function (variante) {
        return variante.options.every(function (wert, i) {
          if (wert === 'Default') return false;
          if (i >= tiefe) return true;
          return !gesucht[i] || wert === gesucht[i];
        });
      });
      if (treffer) return treffer;
    }
    return null;
  }

  function holen(url) {
    if (speicher.has(url)) return speicher.get(url);
    var versprechen = fetch(url).then(function (antwort) {
      if (!antwort.ok) throw new Error('Status ' + antwort.status);
      return antwort.text();
    });
    /* Fehlschläge nicht dauerhaft merken — beim nächsten Versuch neu laden. */
    versprechen.catch(function () {
      speicher.delete(url);
    });
    merken(url, versprechen);
    return versprechen;
  }

  function holenJson(url) {
    return holen(url).then(function (text) {
      return JSON.parse(text);
    });
  }

  /* Bereitet einen Farbwechsel vor, ohne ihn anzuzeigen: Produktdaten holen,
     passende Variante bestimmen, Produktbereich abrufen. Läuft beim Überfahren
     des Farbpunkts, damit beim Klick meist nichts mehr zu warten ist. */
  function vorbereiten(zielUrl, sid) {
    var pfad = zielUrl.split('?')[0];
    var auswahl = auswahlLesen();

    return holenJson(pfad + '.js')
      .then(function (daten) {
        var variante = passendeVariante(daten, auswahl);
        var abruf = pfad + '?section_id=' + encodeURIComponent(sid);
        if (variante) abruf += '&variant=' + variante.id;
        return holen(abruf).then(function (html) {
          return { html: html, ziel: pfad, titel: daten.title, variante: variante };
        });
      });
  }

  /* Inline-Skripte laufen nicht mit, wenn Markup über innerHTML eingesetzt wird.
     Nachgezogen wird deshalb gezielt, was den ausgetauschten Bereich aufbaut:
     die Skripte innerhalb von product-info (u. a. der Options-Picker) und der
     Aufbau des Galerie-Sliders, der ausserhalb davon steht. Alles Übrige der
     Section bleibt unangetastet — es würde sonst ein zweites Mal globale
     Ereignisse registrieren.

     Der Galerie-Aufbau steckt vollständig in einem `DOMContentLoaded`-Handler
     (snippets/od-product-media-gallery-new.liquid). Das Ereignis ist längst
     vorbei und kommt nie wieder — nachgezogen würde der Handler also nur
     registriert und nie ausgeführt, und die Galerie bliebe ohne Slider stehen.
     Deshalb wird `document.addEventListener` für die Dauer der Ausführung so
     umgestellt, dass ein solcher Handler sofort läuft. Das ist unabhängig von
     der genauen Schreibweise im Agentur-Code und übersteht dessen Änderungen.

     `keydown` wird dabei übergangen: Der einzige globale Handler dieser Art
     schliesst die Lightbox und sucht sie sich selbst über den DOM. Er wirkt
     also weiterhin, und ohne diese Ausnahme käme bei jedem Farbwechsel ein
     weiterer hinzu. */
  /* Wunschliste (App «iWish», Link `a.iWishAdd` in der Kaufbox): Die App bindet
     ihren Klick-Handler nur EINMAL beim Seitenstart an die damals vorhandenen
     Links (`iwishAddClick`, markiert sie mit data-iwClick). Nach dem Austausch
     der Section ist der neue Link «nackt» — der Tipp fiel auf `href="#"` durch
     und die Seite sprang zum Anfang (gemeldet 2026-08-23, «lädt neu», nur nach
     einem Farbwechsel). Deshalb hier neu anbinden; dazu den Zustand «schon
     gemerkt» für die neue Variante setzen. Alles defensiv: fehlt die App, passiert
     nichts. */
  function wunschlisteNeuAnbinden(wurzel) {
    var app = window.iWish;
    if (!app || typeof app.iwishAddClick !== 'function') return;
    Array.prototype.forEach.call(wurzel.querySelectorAll('.iWishAdd'), function (link) {
      try {
        app.iwishAddClick(link);
        var variante = link.getAttribute('data-variant');
        if (variante && typeof app.isInWishlist === 'function' && app.isInWishlist(variante)) {
          if (typeof app.setIwishAdded === 'function') app.setIwishAdded(link);
        }
      } catch (fehler) {
        /* App-Interna können sich ändern — dann bleibt es beim Link ohne Handler. */
      }
    });
  }

  /* Höhensprung beim Austausch vermeiden (Nutzer 2026-08-25: «der In-den-
     Warenkorb-Knopf springt während des Ladens kurz»). Gemessen am iPhone-
     Format: Die Galerie ist zwischen dem Einsetzen des Markups und der
     Initialisierung des Sliders rund 50 px niedriger (364 -> 314 -> 364 über
     etwa 180 ms) — mobil steht die Kaufbox darunter, also wandert alles
     darin hoch und gleich wieder zurück. Deshalb die vorherige Höhe als
     Mindesthöhe festhalten und erst freigeben, wenn der Slider steht. */
  function galerieHoeheHalten(wurzel, hoehe) {
    /* Gehalten wird die ganze BILDSPALTE, nicht nur der Galerie-Container:
       zwischen beiden liegen rund 20 px Abstand, die sonst weiterhin kurz
       wegfielen — gemessen 384 -> 364 -> 384 (2026-08-25). */
    var galerie =
      wurzel.querySelector('.product__media-wrapper') ||
      wurzel.querySelector('[id^="product-media-gallery-"]');
    if (!galerie || !hoehe) return;
    galerie.style.minHeight = hoehe + 'px';

    var beobachter = null;
    var netz = 0;
    var erledigt = false;

    function freigeben() {
      if (erledigt) return;
      erledigt = true;
      galerie.style.minHeight = '';
      if (beobachter) beobachter.disconnect();
      if (netz) window.clearTimeout(netz);
    }

    /* Zwei Frames warten: Der erste zeichnet das Layout mit gehaltener Höhe,
       im zweiten hat der Slider seine Masse gesetzt. */
    function baldFreigeben() {
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(freigeben);
      });
    }

    var haupt = galerie.querySelector('[id^="main-swiper-"]');
    if (!haupt) {
      baldFreigeben();
      return;
    }
    if (haupt.classList.contains('swiper-initialized')) {
      baldFreigeben();
      return;
    }
    if (typeof MutationObserver === 'function') {
      beobachter = new MutationObserver(function () {
        if (haupt.classList.contains('swiper-initialized')) baldFreigeben();
      });
      beobachter.observe(haupt, { attributes: true, attributeFilter: ['class'] });
    }
    /* Sicherheitsnetz: Scheitert der Slider, darf die Höhe nicht dauerhaft
       festgenagelt bleiben. */
    netz = window.setTimeout(freigeben, 1500);
  }


  function skripteAusfuehren(wurzel) {
    var kandidaten = Array.from(wurzel.querySelectorAll('script')).filter(function (skript) {
      /* Dateien sind bereits geladen und wirken global — nur die eingebetteten
         Bausteine der Section müssen erneut laufen. JSON sind Daten, kein Code. */
      var typ = (skript.type || '').toLowerCase();
      /* Nur echtes JavaScript: JSON-LD (`application/ld+json`, Produktdaten für
         Google) lief bisher als Code durch und brach mit «Unexpected token ':'» ab
         (2026-08-23). */
      return !skript.src && (typ === '' || typ === 'text/javascript' || typ === 'module');
    });

    /* Zuerst aufräumen, was der vorige Wechsel hinterlassen hat.

       Die Skripte der Section hängen sich bei jedem Durchlauf an `window` und
       `document` (gemessen: rund sieben pro Wechsel — scroll, resize, blur, focus,
       popstate, error, pagehide). Ohne diesen Abbau sammeln sie sich an, laufen
       fortan bei jedem Scrollen und Klicken mit und machen die Seite spürbar zäh.

       Bewusst NICHT blockiert, sondern nur ersetzt: Der Galerie-Slider braucht seinen
       resize-Handler, sonst reagiert er nicht mehr auf Fensterbreiten. Aufgeräumt wird
       ausschliesslich, was aus einem vorherigen Nachzieh-Lauf stammt — was die Seite
       beim ersten Laden registriert hat, bleibt unangetastet. */
    nachgezogeneListener.forEach(function (eintrag) {
      try {
        eintrag.ziel.removeEventListener(eintrag.typ, eintrag.fn, eintrag.optionen);
      } catch (e) {
        /* Ein bereits entfernter Handler ist kein Grund abzubrechen. */
      }
    });
    nachgezogeneListener = [];

    var echtesDokument = document.addEventListener;
    var echtesFenster = window.addEventListener;

    /* `DOMContentLoaded` läuft SOFORT: Das Ereignis ist längst vorbei und kommt nie
       wieder; ein nachgezogener Handler (etwa der komplette Galerie-Aufbau in
       od-product-media-gallery-new.liquid) würde sonst nur registriert und nie
       ausgeführt — die Galerie bliebe ohne Slider zurück. */
    document.addEventListener = function (typ, fn, optionen) {
      if (typ === 'DOMContentLoaded' && typeof fn === 'function') {
        fn();
        return;
      }
      nachgezogeneListener.push({ ziel: document, typ: typ, fn: fn, optionen: optionen });
      return echtesDokument.call(document, typ, fn, optionen);
    };

    window.addEventListener = function (typ, fn, optionen) {
      nachgezogeneListener.push({ ziel: window, typ: typ, fn: fn, optionen: optionen });
      return echtesFenster.call(window, typ, fn, optionen);
    };

    try {
      kandidaten.forEach(function (alt) {
        var neu = document.createElement('script');
        neu.textContent = alt.textContent;
        document.body.appendChild(neu);
        document.body.removeChild(neu);
      });
    } finally {
      document.addEventListener = echtesDokument;
      window.addEventListener = echtesFenster;
    }
  }

  /* Zeitmessung der Teilschritte, abrufbar über `window.wbColorSwapZeiten`.
     Kostet nichts Nennenswertes und erspart bei jeder künftigen Änderung das
     Raten, welcher Schritt die Wartezeit verursacht. */
  var zeiten = {};
  window.wbColorSwapZeiten = zeiten;

  /* Die Stylesheet-Links der Section bleiben beim Austausch stehen.

     Die Section bindet ihre CSS-Dateien selbst ein — `section-main-product.css`,
     `component-price.css` usw., neun `<link rel="stylesheet">` direkt unter der
     Section. Wird ihr Markup per `innerHTML` ersetzt, entstehen diese Links neu, und
     der Browser lädt ihre Sheets asynchron nach, auch aus dem Cache: gemessen rund
     100 ms, in denen KEINE Regel aus diesen Dateien gilt. Der Kaufspalte fehlte
     dabei ihr `padding-left: 5rem`, sie stand 48 px weiter links und sprang dann
     zurück (gemeldet 2026-08-27: «Zahlarten und „Mehr als …“ verschieben sich
     beim Laden»). Verschieben hilft nicht — auch ein innerhalb des Dokuments
     umgehängter Link verliert sein Sheet (gemessen).

     Deshalb bleiben die vorhandenen Links unangetastet an ihrem Platz; ersetzt wird
     nur der Rest, in der ursprünglichen Reihenfolge, damit die Kaskade dieselbe
     bleibt. Links, die im neuen Markup fehlen, werden entfernt; neue eingesetzt.
     Die Knoten zwischen zwei Links gehen als EIN Fragment hinein, damit die
     Custom Elements — wie bei `innerHTML` — erst aufwachen, wenn ihre Geschwister
     schon im Dokument stehen. Eingesetzt werden Kopien (`importNode`), damit das
     geparste Markup für `skripteAusfuehren` unverändert bleibt. */
  function inhaltErsetzen(alt, neu) {
    function istStylesheet(knoten) {
      return (
        knoten.nodeType === 1 &&
        knoten.tagName === 'LINK' &&
        knoten.relList &&
        knoten.relList.contains('stylesheet')
      );
    }

    var behalten = Array.prototype.filter.call(alt.children, istStylesheet);

    Array.prototype.slice.call(alt.childNodes).forEach(function (knoten) {
      if (behalten.indexOf(knoten) === -1) alt.removeChild(knoten);
    });

    var i = 0;
    var sammler = document.createDocumentFragment();

    function sammlerEinsetzen() {
      if (sammler.childNodes.length) {
        alt.insertBefore(sammler, i < behalten.length ? behalten[i] : null);
      }
    }

    Array.prototype.slice.call(neu.childNodes).forEach(function (knoten) {
      if (istStylesheet(knoten)) {
        var href = knoten.getAttribute('href');
        for (var j = i; j < behalten.length; j++) {
          if (behalten[j].getAttribute('href') === href) {
            sammlerEinsetzen();
            /* Alte Links davor, die im neuen Markup fehlen, sind überholt. */
            while (i < j) alt.removeChild(behalten[i++]);
            i = j + 1;
            return;
          }
        }
      }
      sammler.appendChild(document.importNode(knoten, true));
    });

    sammlerEinsetzen();
    while (i < behalten.length) alt.removeChild(behalten[i++]);
  }

  function ausfuehren(daten, sid, mitHistorie) {
    var zeitStart = performance.now();
    var neu = new DOMParser().parseFromString(daten.html, 'text/html');
    var zeitGeparst = performance.now();

    /* Ausgetauscht wird die GESAMTE Section, nicht nur Kaufbereich und Galerie.
       Produktabhängig sind auch technische Daten, die Beschreibungs-Akkordeons, das
       Varianten-Slideover und die Auszeichnungen — sie liegen ausserhalb von
       `product-info` und blieben sonst auf dem vorherigen Artikel stehen. Genau
       daraus entstanden fremde Optionsfelder im Kaufbereich. */
    var wurzelAlt = document.getElementById(SECTION_PRAEFIX + sid);
    var wurzelNeu = neu.getElementById(SECTION_PRAEFIX + sid);

    /* Fehlt die Section, stimmt etwas nicht — dann lieber gar nicht eingreifen,
       damit keine halbe Produktseite stehen bleibt. */
    if (!wurzelAlt || !wurzelNeu) return false;

    /* Die alte Slider-Instanz zeigt gleich auf entfernte Knoten. */
    if (window.mainswiper && typeof window.mainswiper.destroy === 'function') {
      try {
        window.mainswiper.destroy(true, true);
      } catch (e) {
        /* Eine bereits zerstörte Instanz ist kein Grund abzubrechen. */
      }
    }

    /* Für das mobile Bild-Kleben (sections/main-product.liquid): merken, ob das
       Bild gerade fixiert war — der Austausch wirft Bild und Platzhalter weg,
       und im Hysterese-Randbereich würde das neue Bild sonst nicht wieder
       fixiert («beim Wechseln verschwindet das Bild»). */
    window.wbBildWarFixiert = wurzelAlt.querySelector('.wb-bild-fixiert')
      ? performance.now()
      : 0;

    /* Klebt gerade ein Bild? Dann dessen Klon stehen lassen, bis das neue
       Bild fixiert UND geladen ist — sonst blitzt beim Austausch Weiss auf
       (gleiches Rezept wie beim Optionswechsel, sections/main-product.liquid). */
    if (window.wbBildWarFixiert && window.wbKlonZeigen) window.wbKlonZeigen();

    /* Vor dem Ersetzen die Galerie-Hoehe merken (siehe galerieHoeheHalten). */
    var galerieAlt =
      wurzelAlt.querySelector('.product__media-wrapper') ||
      wurzelAlt.querySelector('[id^="product-media-gallery-"]');
    var galerieHoehe = galerieAlt ? Math.round(galerieAlt.getBoundingClientRect().height) : 0;

    /* Produktname und Intro-Text NICHT mitwechseln (Nutzerwunsch 2026-08-26):
       Die Farbartikel sind dasselbe Modell, ihre Texte unterscheiden sich nur
       um Kleinigkeiten — beim Klick auf einen Farbpunkt sprang der Text
       trotzdem sichtbar um und lenkte vom Bild ab. Die alten Knoten werden
       deshalb vor dem Austausch gesichert und danach wieder eingesetzt.
       Bewusst in Kauf genommen: Steht im Namen des Zielartikels etwas anderes,
       bleibt der bisherige Text stehen. Beim normalen Seitenaufruf (und nach
       einem Reload) zeigt jeder Artikel weiterhin seinen eigenen Text. */
    var texteBehalten = {};
    ['.wb-buybox-title', '.wb-ph-intro'].forEach(function (waehler) {
      var knoten = wurzelAlt.querySelector(waehler);
      if (knoten) texteBehalten[waehler] = knoten.innerHTML;
    });

    inhaltErsetzen(wurzelAlt, wurzelNeu);

    Object.keys(texteBehalten).forEach(function (waehler) {
      var ziel = wurzelAlt.querySelector(waehler);
      if (ziel) ziel.innerHTML = texteBehalten[waehler];
    });

    galerieHoeheHalten(wurzelAlt, galerieHoehe);
    var zeitEingesetzt = performance.now();

    auswahlfelderAngleichen(daten.variante);

    /* Der Lieferkasten (snippets/wb-delivery-box.liquid) hängt an der Variante
       und wird beim Angleichen nicht serverseitig neu gerendert — er hört auf
       dieses Ereignis und blendet die passende vorgerenderte Fassung ein. */
    document.dispatchEvent(
      new CustomEvent('wb:variantenwechsel', {
        detail: { variantId: daten.variante && daten.variante.id },
      }),
    );

    skripteAusfuehren(wurzelNeu);
    var zeitSkripte = performance.now();

    /* Ohne diese Zeile stehen im Modus «Business» Bruttopreise — derselbe Fehler
       steckte bis 2026-08-18 in der Suchvorschau. */
    if (typeof window.changePrice === 'function' && typeof window.getStoredPriceOption === 'function') {
      window.changePrice(window.getStoredPriceOption());
    }

    wunschlisteNeuAnbinden(wurzelAlt);

    if (mitHistorie) {
      window.history.pushState({ wbColorSwap: true }, '', daten.ziel);
    }
    if (daten.titel) document.title = daten.titel;

    /* Der Fokus lag auf einem Element, das es nicht mehr gibt. Für Tastatur- und
       Screenreader-Bedienung zurück auf den nun aktiven Farbpunkt. */
    var aktiv = document.querySelector('.crossseller-wrapper a[href^="' + daten.ziel + '"]');
    if (aktiv && document.activeElement === document.body) aktiv.focus({ preventScroll: true });

    melden(daten.titel);
    tooltipsAusrichten();

    zeiten.parsen = Math.round(zeitGeparst - zeitStart);
    zeiten.einsetzen = Math.round(zeitEingesetzt - zeitGeparst);
    zeiten.skripte = Math.round(zeitSkripte - zeitEingesetzt);
    zeiten.rest = Math.round(performance.now() - zeitSkripte);
    zeiten.gesamt = Math.round(performance.now() - zeitStart);

    return true;
  }

  /* Die Auswahlfelder auf die tatsächlich geladene Variante stellen.

     Nötig wegen eines Fehlers im Theme: `snippets/product-variant-options.liquid`
     setzt `selected="selected"` über `option.selected_value == value`, und für die
     erste Option greift dieser Vergleich nicht — im gelieferten Markup fehlt das
     Attribut, worauf der Browser den ersten Eintrag der Liste anzeigt. Sichtbar wurde
     das als Widerspruch: Bild, Preis und Artikelnummer gehörten zur weissen
     Gestellfarbe, das Feld zeigte «Schwarz».

     Der Fehler steckt nicht im Farbwechsel — auch ein normaler Aufruf mit
     `?variant=…` zeigt die falsche Farbe (dort korrigiert sich das Theme selbst,
     indem es die Variante auf den angezeigten Wert umschreibt). Hier wird deshalb
     die Anzeige an die geladene Variante angeglichen, ohne ein `change`-Ereignis
     auszulösen — das würde einen weiteren Abruf starten. */
  function auswahlfelderAngleichen(variante) {
    if (!variante || !variante.options) return;

    var felder = document.querySelectorAll('variant-selects [data-option-name]');
    felder.forEach(function (feld, index) {
      var wert = variante.options[index];
      if (!wert || wert === 'Default') return;

      var select = feld.querySelector('select');
      if (select && select.value !== wert) select.value = wert;

      var radio = feld.querySelector('input[type="radio"][value="' + CSS.escape(wert) + '"]');
      if (radio && !radio.checked) radio.checked = true;

      /* Manche Felder zeigen den gewählten Wert zusätzlich in der Beschriftung. */
      var anzeige = feld.querySelector('[data-selected-swatch-value]');
      if (anzeige) anzeige.textContent = wert;
    });
  }

  /* Ankündigung für Screenreader. Bewusst nur der Produkttitel: Der ist bereits
     übersetzt, dadurch braucht das Skript keinen eigenen Text in vier Sprachen. */
  function melden(titel) {
    if (!titel) return;
    var region = document.getElementById('wb-color-swap-status');
    if (!region) {
      region = document.createElement('div');
      region.id = 'wb-color-swap-status';
      region.className = 'visually-hidden';
      region.setAttribute('aria-live', 'polite');
      region.setAttribute('role', 'status');
      document.body.appendChild(region);
    }
    region.textContent = titel;
  }

  /* Den Farbnamen-Tooltip im Fenster halten.

     Der Tooltip (snippets/wb-color-dot.liquid) wird über seinem Punkt zentriert.
     Beim ersten Punkt einer Reihe ragt er dadurch links aus dem Fenster, beim
     letzten rechts — sichtbar als «ubanitgrau» statt «Cubanitgrau». Reines CSS kann
     das nicht lösen: Der Tooltip kennt den Fensterrand nicht, und welcher Punkt in
     einer umbrechenden Reihe ganz links steht, hängt von der Fensterbreite ab.

     Deshalb wird hier gemessen. Jeder Tooltip wird kurz unsichtbar ins Layout geholt
     (`wb-tooltip--messen`), seine Lage mit dem Fenster verglichen und die nötige
     Verschiebung als `--wb-tooltip-shift` hinterlegt; das CSS im Snippet
     wb-color-swap rechnet sie in die Zentrierung ein und lässt die Pfeilspitze
     weiter auf den Punkt zeigen. Erst alle Klassen setzen, dann alle messen, dann
     alle entfernen — so rechnet der Browser das Layout einmal statt pro Punkt.

     Aufgerufen beim Start, nach jedem Farbwechsel (neue Punkte), nach dem Laden der
     Schriften (andere Textbreite) und bei geänderter Fensterbreite. Läuft auch auf
     pCon-Seiten, die vom Farbwechsel selbst ausgenommen sind. */
  var TOOLTIP_RAND = 8;

  function tooltipsAusrichten() {
    var tooltips = Array.from(document.querySelectorAll('.crossseller-wrapper .b-tooltip'));
    if (!tooltips.length) return;

    tooltips.forEach(function (tooltip) {
      tooltip.style.setProperty('--wb-tooltip-shift', '0px');
      tooltip.classList.add('wb-tooltip--messen');
    });

    var fensterBreite = document.documentElement.clientWidth;
    var verschiebungen = tooltips.map(function (tooltip) {
      var lage = tooltip.getBoundingClientRect();
      if (lage.width === 0) return 0;
      if (lage.left < TOOLTIP_RAND) return TOOLTIP_RAND - lage.left;
      if (lage.right > fensterBreite - TOOLTIP_RAND) return fensterBreite - TOOLTIP_RAND - lage.right;
      return 0;
    });

    tooltips.forEach(function (tooltip, i) {
      tooltip.style.setProperty('--wb-tooltip-shift', Math.round(verschiebungen[i]) + 'px');
      tooltip.classList.remove('wb-tooltip--messen');
    });
  }

  var tooltipTimer = null;
  function tooltipsSpaeterAusrichten() {
    if (tooltipTimer) window.clearTimeout(tooltipTimer);
    tooltipTimer = window.setTimeout(tooltipsAusrichten, 150);
  }

  /* Die Klasse sitzt an der Section und nicht an `.product`: Letzteres wird beim
     Austausch selbst ersetzt und verlöre die Markierung mitten im Vorgang. */
  function ladeanzeige(an) {
    var wurzel = document.querySelector('[id^="' + SECTION_PRAEFIX + '"]');
    if (wurzel) wurzel.classList.toggle('wb-color-swap--laedt', an);
  }

  function wechseln(zielUrl, sid, mitHistorie) {
    laufendeNummer += 1;
    var meineNummer = laufendeNummer;
    ladeanzeige(true);

    return vorbereiten(zielUrl, sid)
      .then(function (daten) {
        /* Zwischenzeitlich wurde eine andere Farbe angeklickt — diese Antwort ist
           überholt und darf nichts mehr anfassen. */
        if (meineNummer !== laufendeNummer) return;

        var erfolg = ausfuehren(daten, sid, mitHistorie);
        ladeanzeige(false);
        if (!erfolg) window.location.href = zielUrl;
      })
      .catch(function () {
        if (meineNummer !== laufendeNummer) return;

        /* Netzwerkfehler, fremdes Template (404) oder unerwartetes Markup:
           Der Kunde merkt davon nichts, es wird einfach normal navigiert. */
        ladeanzeige(false);
        window.location.href = zielUrl;
      });
  }

  function farbpunktAus(ereignis) {
    var ziel = ereignis.target;
    var link = ziel && ziel.closest ? ziel.closest('.crossseller-wrapper a[href]') : null;
    return link;
  }

  function einfacherKlick(ereignis) {
    return (
      ereignis.button === 0 &&
      !ereignis.metaKey &&
      !ereignis.ctrlKey &&
      !ereignis.shiftKey &&
      !ereignis.altKey
    );
  }

  function start() {
    var sid = sectionId();
    if (!sid || istPconSeite(sid)) return;

    /* Vorabladen, sobald sich Interesse abzeichnet. Zwischen Überfahren und Klick
       liegen typisch 200–400 ms; der Abruf dauert rund 600 ms und ist damit beim
       Loslassen meist fertig. Auf Touch-Geräten übernimmt das pointerdown. */
    ['pointerenter', 'pointerdown'].forEach(function (art) {
      document.addEventListener(
        art,
        function (ereignis) {
          var link = farbpunktAus(ereignis);
          if (!link) return;
          var url = link.getAttribute('href');
          if (!url || url === window.location.pathname) return;
          vorbereiten(url, sid).catch(function () {
            /* Scheitert das Vorabladen, versucht es der Klick erneut. */
          });
        },
        true,
      );
    });

    document.addEventListener('click', function (ereignis) {
      var link = farbpunktAus(ereignis);
      if (!link || !einfacherKlick(ereignis)) return;

      var url = link.getAttribute('href');
      if (!url) return;

      /* Klick auf die bereits angezeigte Farbe: nichts tun, aber auch nicht neu laden. */
      if (url.split('?')[0] === window.location.pathname) {
        ereignis.preventDefault();
        return;
      }

      ereignis.preventDefault();
      wechseln(url, sid, true);
    });

    /* Zurück und Vorwärts im Browser sollen zwischen den Farben wechseln statt die
       Seite zu verlassen. Die Adresse hat der Browser bereits gesetzt; wir stellen
       nur den passenden Inhalt her. Ist das Ziel keine Farbe dieser Familie,
       überlassen wir es dem Browser. */
    window.addEventListener('popstate', function () {
      var pfad = window.location.pathname;
      var bekannt = Array.from(document.querySelectorAll('.crossseller-wrapper a[href]')).some(function (a) {
        return a.getAttribute('href').split('?')[0] === pfad;
      });
      if (!bekannt) return;
      wechseln(pfad, sid, false);
    });
  }

  function tooltipsStarten() {
    tooltipsAusrichten();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(tooltipsAusrichten);
    window.addEventListener('resize', tooltipsSpaeterAusrichten);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      tooltipsStarten();
      start();
    });
  } else {
    tooltipsStarten();
    start();
  }
})();
